import type { AgentPageElement } from './page-task-protocol';
import { AGENT_PAGE_EXCLUDE, readAgentPageContent } from './page-content';

const RECORD = '[data-agent-record],tr,[role="row"],li,[role="listitem"]';
const REGION =
  '[data-agent-region],section[aria-label],[role="region"],[role="search"],form[aria-label],nav[aria-label]';

/** 只读取可见语义；业务标记补足普通卡片，标准表格通过表头关联字段。 */
export function readAgentPageSemantics(element: HTMLElement): AgentPageElement['semantics'] {
  const text = (node: Element | null) => {
    if (!node || node.closest(AGENT_PAGE_EXCLUDE)) return '';
    const content = readAgentPageContent('workspace', node, 500);
    return content.truncated
      ? ''
      : content.text
          .replace(/\[p\d+\] /g, '')
          .replace(/\s+/g, ' ')
          .trim();
  };
  const name = (node: Element | null) => {
    if (!node || node.closest(AGENT_PAGE_EXCLUDE)) return '';
    return (
      node.getAttribute('aria-label') ||
      node
        .getAttribute('aria-labelledby')
        ?.split(/\s+/)
        .map((id) => text(document.getElementById(id)))
        .join(' ') ||
      node.getAttribute('data-agent-region') ||
      ''
    ).slice(0, 240);
  };
  const region = name(element.closest(REGION));
  const record = element.closest(RECORD);
  const recordType = record?.getAttribute('data-agent-record')?.slice(0, 120);
  const fields: { name: string; value: string }[] = [];
  if (record) {
    for (const field of record.querySelectorAll('[data-agent-field]')) {
      if (fields.length >= 20) break;
      if (field.closest(RECORD) !== record || field.closest(AGENT_PAGE_EXCLUDE)) continue;
      const key = field.getAttribute('data-agent-field') ?? '';
      if (/password|secret|token|cookie|api.?key/i.test(key)) continue;
      // 折叠字段可提供完整的只读语义值，避免为查找而聚焦悬浮卡片。
      const explicitValue = field.getAttribute('data-agent-value');
      const value = explicitValue
        ? explicitValue.length > 2000 ? `${explicitValue.slice(0, 1999)}…` : explicitValue
        : text(field);
      if (key && value) fields.push({ name: key.slice(0, 120), value });
    }
    if (!fields.length && record.matches('tr,[role="row"]')) {
      const table = record.closest('table,[role="table"],[role="grid"]');
      const headers = table
        ? Array.from(table.querySelectorAll('th,[role="columnheader"]')).filter(
            (header) => header.closest('table,[role="table"],[role="grid"]') === table,
          )
        : [];
      const cells = Array.from(
        record.querySelectorAll('td,[role="cell"],[role="gridcell"]'),
      ).filter((cell) => cell.closest(RECORD) === record);
      for (const [index, cell] of cells.slice(0, 20).entries()) {
        const header =
          cell
            .getAttribute('headers')
            ?.split(/\s+/)
            .map((id) => text(document.getElementById(id)))
            .join(' ') || text(headers[index] ?? null);
        const value = text(cell);
        if (header && value && !/password|secret|token|cookie|api.?key/i.test(header))
          fields.push({ name: header.slice(0, 120), value });
      }
    }
  }
  const purpose =
    element.getAttribute('data-agent-purpose') ??
    (element.matches('input[type="search"],[role="searchbox"],button[data-search-button],button[data-agent-purpose="search"],[role="search"] button')
      ? 'search'
      : undefined);
  const field = element.getAttribute('data-agent-field')?.slice(0, 120);
  const semantics: NonNullable<AgentPageElement['semantics']> = {
    ...(region ? { region } : {}),
    ...(recordType ? { record: recordType } : {}),
    ...(fields.length ? { fields } : {}),
    ...(field ? { field } : {}),
    ...(purpose === 'search' || purpose === 'filter' || purpose === 'sort' ? { purpose } : {}),
    ...(element.hasAttribute('aria-expanded')
      ? { expanded: element.getAttribute('aria-expanded') === 'true' }
      : {}),
    ...(element.hasAttribute('aria-current')
      ? { current: element.getAttribute('aria-current') !== 'false' }
      : {}),
  };
  return Object.keys(semantics).length ? semantics : undefined;
}
